import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

interface FlowNode {
  id: string;
  label: string;
  description?: string;
  type?: 'start' | 'end' | 'process' | 'decision' | 'input' | 'output';
  color?: string;
}

interface FlowEdge {
  from: string;
  to: string;
  label?: string;
}

interface FlowchartVisualizationProps {
  data: {
    nodes: FlowNode[];
    edges: FlowEdge[];
  };
  config: {
    title?: string;
    description?: string;
    explanation?: string;
    direction?: 'vertical' | 'horizontal';
  };
  preview?: boolean;
  collapsed?: boolean;
}

const NODE_COLORS: Record<string, string> = {
  start: 'hsl(var(--chart-7))',
  end: 'hsl(var(--chart-1))',
  process: 'hsl(var(--chart-6))',
  decision: 'hsl(var(--chart-8))',
  input: 'hsl(var(--chart-9))',
  output: 'hsl(var(--chart-10))',
};

const FlowchartVisualization: React.FC<FlowchartVisualizationProps> = ({
  data,
  config,
  preview = false,
  collapsed = false,
}) => {
  const isHorizontal = config.direction === 'horizontal';
  const { nodeMap, outgoingEdges, rootNodeIds } = useMemo(() => {
    const nodes = data?.nodes || [];
    const edges = data?.edges || [];
    const nodesById = new Map(nodes.map(node => [node.id, node]));
    const edgesBySource = new Map<string, FlowEdge[]>();
    const inDegree = new Map<string, number>();

    nodes.forEach(node => {
      edgesBySource.set(node.id, []);
      inDegree.set(node.id, 0);
    });

    edges.forEach(edge => {
      if (!nodesById.has(edge.from) || !nodesById.has(edge.to)) return;
      edgesBySource.get(edge.from)?.push(edge);
      inDegree.set(edge.to, (inDegree.get(edge.to) || 0) + 1);
    });

    const discovered = new Set<string>();
    const rootIds = nodes
      .filter(node => (inDegree.get(node.id) || 0) === 0)
      .map(node => node.id);

    if (rootIds.length === 0 && nodes[0]) {
      rootIds.push(nodes[0].id);
    }

    const visit = (nodeId: string) => {
      if (discovered.has(nodeId)) return;
      discovered.add(nodeId);
      (edgesBySource.get(nodeId) || []).forEach(edge => visit(edge.to));
    };

    rootIds.forEach(visit);
    nodes.forEach(node => {
      if (!discovered.has(node.id)) {
        rootIds.push(node.id);
        visit(node.id);
      }
    });

    return {
      nodeMap: nodesById,
      outgoingEdges: edgesBySource,
      rootNodeIds: rootIds,
    };
  }, [data?.edges, data?.nodes]);

  if (!data?.nodes || data.nodes.length === 0) {
    return <div className="viz-unsupported">No flowchart data provided</div>;
  }

  const getNodeShape = (type?: string) => {
    switch (type) {
      case 'start': return 'viz-flow-node-pill';
      case 'end': return 'viz-flow-node-pill';
      case 'decision': return 'viz-flow-node-diamond';
      default: return 'viz-flow-node-rect';
    }
  };

  const renderNode = (nodeId: string, ancestorPath = new Set<string>()): React.ReactNode => {
    const node = nodeMap.get(nodeId);
    if (!node) return null;

    const nodeColor = node.color || NODE_COLORS[node.type || 'process'] || NODE_COLORS.process;
    const nextPath = new Set(ancestorPath);
    nextPath.add(nodeId);
    const edges = (outgoingEdges.get(nodeId) || []).filter(edge => !ancestorPath.has(edge.to));

    return (
      <div
        className={`viz-flow-branch ${isHorizontal ? 'viz-flow-branch-horizontal' : 'viz-flow-branch-vertical'}`}
        key={`${nodeId}-${Array.from(ancestorPath).join(':') || 'root'}`}
      >
        <div
          className={`viz-flow-node ${getNodeShape(node.type)}`}
          style={{ borderColor: nodeColor, ['--node-color' as any]: nodeColor }}
        >
          <div className="viz-flow-node-label">{node.label}</div>
          {node.description && !preview && (
            <div className="viz-flow-node-desc">{node.description}</div>
          )}
        </div>

        {edges.length > 0 && (
          <div
            className={`viz-flow-children ${isHorizontal ? 'viz-flow-children-horizontal' : 'viz-flow-children-vertical'} ${edges.length > 1 ? 'viz-flow-children-split' : ''}`}
          >
            {edges.map((edge) => (
              <div className="viz-flow-child" key={`${nodeId}-${edge.to}-${edge.label || 'edge'}`}>
                <div className={`viz-flow-connector ${isHorizontal ? 'viz-flow-connector-h' : 'viz-flow-connector-v'}`}>
                  <div className="viz-flow-arrow-line" />
                  <div className="viz-flow-arrow-head" />
                  {edge.label && <span className="viz-flow-edge-label">{edge.label}</span>}
                </div>
                {renderNode(edge.to, nextPath)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`viz-flowchart-wrapper ${preview ? 'viz-preview' : ''}`}>
      {!preview && config.title && (
        <div className="viz-flowchart-header">
          <h3 className="viz-flowchart-title">{config.title}</h3>
          {config.description && <p className="viz-flowchart-description">{config.description}</p>}
        </div>
      )}
      <motion.div
        initial={false}
        animate={{
          height: collapsed ? 0 : 'auto',
          opacity: collapsed ? 0 : 1,
        }}
        transition={{
          height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          opacity: { duration: 0.2, ease: 'easeOut' },
        }}
        style={{ overflow: 'hidden' }}
      >
        <div className={`viz-flowchart ${isHorizontal ? 'viz-flowchart-horizontal' : 'viz-flowchart-vertical'}`}>
          {rootNodeIds.map((rootNodeId) => renderNode(rootNodeId))}
        </div>
        {!preview && config.explanation && (
          <div className="viz-flowchart-footer">
            <span>{config.explanation}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default FlowchartVisualization;
